/* eslint-disable no-constant-condition */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/jsx-wrap-multilines */
import React, { FC, ReactNode, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import PageHeader from '@salesforce/design-system-react/components/page-header';
import PageHeaderControl from '@salesforce/design-system-react/components/page-header/control';
import ButtonGroup from '@salesforce/design-system-react/components/button-group';
import Button from '@salesforce/design-system-react/components/button';
import Input from '@salesforce/design-system-react/components/input';
import InputIcon from '@salesforce/design-system-react/components/icon/input-icon';
import Popover from '@salesforce/design-system-react/components/popover';
import { getEventIcon } from '../../../../util/utilityFunctions';
import ExpenseInventoryForm from '../form/ExpenseInventoryForm';
import { getUserInfo } from '../../../../store/ducks/userInfo';

interface Props {
  handleSearch: (event: any, val: { value: string }) => void;
  handleFilter: () => void;
  type?: string;
  resetFilters: () => void;
  filterPresets: FilterPreset[];
  onEditPress: (filter: FilterPreset, isEditPress: boolean) => void;
  handleFilterEvents: (filterValues: any, filterName: string) => void;
  selectedFilterApiValues: any;
  refetch?: () => void;
}

interface UserInfo {
  assignedScreens: any;
}
interface FilterPreset {
  filterValues: any;
  filterExternalId: string;
  filterName: string;
}

const ExpenseInventoryListHeader: FC<Props> = ({
  handleSearch,
  handleFilter,
  resetFilters,
  onEditPress,
  handleFilterEvents,
  filterPresets,
  selectedFilterApiValues,
  refetch,
}) => {
  const filtersLength = selectedFilterApiValues ? Object.keys(selectedFilterApiValues).length : 0;
  const userInfo: UserInfo | null = useSelector(getUserInfo);
  const [isOpen, setIsOpen] = useState(false);
  const [filterPresetOpen, setFilterPresetOpen] = useState(false);

  const handleNewExpenseInventory = (): void => {
    setIsOpen(true);
  };

  const handleModalToggle = useCallback((): void => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const requiredForms = userInfo?.assignedScreens;

  const handleFilterPresetPopover = useCallback((): void => {
    setFilterPresetOpen(!filterPresetOpen);
  }, [filterPresetOpen]);

  const handleFilterPresetPopoverClose = useCallback((): void => {
    setFilterPresetOpen(false);
  }, []);

  const handleFilterEventItems = (filterValues: any, filterName: string) => (): void => {
    setFilterPresetOpen(false);
    handleFilterEvents(filterValues, filterName);
  };

  const handleEditPress = (filter: FilterPreset) => (): void => {
    setFilterPresetOpen(false);
    onEditPress(filter, true);
  };

  const headerActions = (): ReactNode => (
    <PageHeaderControl className="slds-m-left_large">
      <ButtonGroup variant="list" id="button-group-page-header-actions">
        {requiredForms?.find(
          (rec: any) => rec.screen === 'Expense Inventory - Create' && rec.isRequired
        ) && (
          <Button
            iconCategory="utility"
            className="slds-text-color_default"
            label="New"
            iconName="add"
            iconPosition="left"
            onClick={handleNewExpenseInventory}
          />
        )}
      </ButtonGroup>
    </PageHeaderControl>
  );

  const headerControls = (): ReactNode => (
    <>
      <PageHeaderControl className="slds-m-left_large">
        <Input
          iconRight={
            <InputIcon
              assistiveText={{
                icon: 'Search',
              }}
              name="search"
              category="utility"
            />
          }
          id="search-by-case"
          onChange={(_event: any, searchItem: { value: string }): void =>
            handleSearch(_event, searchItem)
          }
          placeholder="Search"
        />
      </PageHeaderControl>
      <PageHeaderControl className="slds-m-left_large">
        <ButtonGroup variant="list" id="button-group-page-header-controls">
          <Button
            iconCategory="utility"
            className="slds-text-color_default"
            iconName="filterList"
            label="Filter"
            iconPosition="left"
            onClick={handleFilter}
          />
          <div className="slds-dropdown-trigger slds-dropdown-trigger_click filter-preset-popover">
            <Popover
              align="bottom"
              isOpen={filterPresetOpen}
              onClose={handleFilterPresetPopoverClose}
              onRequestClose={handleFilterPresetPopoverClose}
              ariaLabelledby=""
              body={
                <div className="filter-preset_dropdown">
                  <ul className="slds-dropdown__list" role="menu" aria-label="Show More">
                    <li className="slds-dropdown__item" role="presentation" key="reset">
                      <Button
                        className="slds-button slds-button_neutral filter-preset-reset-button"
                        onClick={resetFilters}
                      >
                        Reset
                      </Button>
                    </li>
                    {filterPresets &&
                      filterPresets.map((filter: FilterPreset) => {
                        return (
                          <li
                            className="slds-dropdown__item"
                            role="presentation"
                            key={filter.filterExternalId}
                          >
                            <Button
                              className="slds-button slds-button_neutral filter-preset-heading-button"
                              onClick={handleFilterEventItems(filter.filterValues, filter.filterName)}
                            >
                              {filter.filterName}
                            </Button>
                            <Button
                              className="slds-button slds-button_neutral filter-preset-edit-button"
                              onClick={handleEditPress(filter)}
                            >
                              Edit
                            </Button>
                          </li>
                        );
                      })}
                  </ul>
                </div>
              }
              id="popover-walkthrough"
            >
              <Button
                className="filter-drop-trigger"
                iconCategory="utility"
                iconName="filter"
                iconPosition="left"
                label="Filter Presets"
                onClick={handleFilterPresetPopover}
              />
            </Popover>
          </div>
        </ButtonGroup>
      </PageHeaderControl>
    </>
  );

  const iconPath = getEventIcon('Inventory');

  return (
    <div>
      <IconSettings iconPath="/icons">
        <PageHeader
          className="events_header"
          icon={<img src={iconPath} className="logo-image" alt="expense inventory" />}
          label="Inventory"
          onRenderActions={headerActions}
          onRenderControls={headerControls}
          title="Expense Inventory"
          truncate
          variant="object-home"
          info={
            <span key="filters">
              {`${0} items ${filtersLength ? `• ${filtersLength} Filters ` : ''}`}
              {filtersLength && (
                <Button key="clear" className="refresh_button" onClick={resetFilters}>
                  Clear
                </Button>
              )}
            </span>
          }
        />
      </IconSettings>
      {isOpen && (
        <ExpenseInventoryForm isOpen={isOpen} toggleOpen={handleModalToggle} refetch={refetch} />
      )}
    </div>
  );
};

export default ExpenseInventoryListHeader;
